import { useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import { X } from 'react-bootstrap-icons'
import { useTranslation } from 'react-i18next'
import { useUser } from '../../contexts/UserContext'
import { analyticsService } from '../../services/analyticsService'
import { isAndroid, isIOS, isStandalone } from '../../utils/platform'
import { APP_PROMO_BANNER_DISMISSED_KEY, APP_PROMO_EVENTS, dismiss, isDismissed, storeUrlForPlatform } from './appPromo'
import styles from './AppPromoBanner.module.css'

/**
 * In-app native-app promo banner (#1465): sticky bar above the header.
 * Shown for Android (browser + installed PWA) and iOS installed PWA. iOS *browser* is
 * intentionally excluded — the native Safari Smart App Banner (apple-itunes-app meta tag
 * in index.html) covers that case, and showing both would double up.
 *
 * Gate (all must hold): player-facing (Layout also wraps admin routes), a supported
 * platform/mode per above, and not previously dismissed.
 *
 * Lives in Layout (every route), so a module-level flag fires the "shown" event and plays
 * the slide-down only once per session, not on every in-app navigation.
 */
let bannerShownThisSession = false

const AppPromoBanner: React.FC = () => {
  const { t } = useTranslation('promo')
  const { hasAdminAccess, hasRole } = useUser()
  const isPlayerOnly = !hasAdminAccess && !hasRole('CADDIEMASTER')
  const [dismissed, setDismissed] = useState(() => isDismissed(APP_PROMO_BANNER_DISMISSED_KEY))
  const [firstShow] = useState(() => !bannerShownThisSession)

  const platform = isIOS() ? 'ios' : 'android'
  const shouldShow = isPlayerOnly && !dismissed && (isAndroid() || (isIOS() && isStandalone()))

  useEffect(() => {
    if (shouldShow && !bannerShownThisSession) {
      bannerShownThisSession = true
      analyticsService.track(APP_PROMO_EVENTS.bannerShown, { platform, standalone: isStandalone() })
    }
  }, [shouldShow, platform])

  if (!shouldShow) return null

  const handleInstallClick = () => {
    analyticsService.track(APP_PROMO_EVENTS.bannerClicked, { platform, standalone: isStandalone() })
  }

  const handleDismiss = () => {
    dismiss(APP_PROMO_BANNER_DISMISSED_KEY)
    setDismissed(true)
    analyticsService.track(APP_PROMO_EVENTS.bannerDismissed, { platform, standalone: isStandalone() })
  }

  return (
    <div className={`${styles.banner} ${firstShow ? styles.animateIn : ''}`}>
      <img src="/android-chrome-192x192.png" alt="" className={styles.icon} />
      <div className={styles.content}>
        <div className={styles.appName}>{t('app_name')}</div>
        <div className={styles.copy}>{t('app_banner_copy')}</div>
      </div>
      <Button
        href={storeUrlForPlatform()}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.installButton}
        onClick={handleInstallClick}
      >
        {t('app_banner_install')}
      </Button>
      <button
        type="button"
        className={styles.dismissButton}
        onClick={handleDismiss}
        aria-label={t('app_banner_dismiss')}
      >
        <X size={24} />
      </button>
    </div>
  )
}

export default AppPromoBanner
